/**
 * crashDedupService
 *
 * Per-session, signature-keyed dedup for crash events. Governs whether each
 * crash opens a dialog, triggers a silent background send, renders the inline
 * recovery fallback, or is dropped entirely. State is in-memory only — resets
 * on app launch.
 *
 * Signature key: `${category}::${sanitizedMessage}`.
 *
 * See Docs/superpowers/specs/2026-04-23-crash-dialog-auto-send-and-dedup-design.md
 * for the full decision matrix.
 */

import type {
  CrashContext,
  CrashOccurredArgs,
  CrashOccurredPayload,
  CrashReportEnrichment,
  CrashReportMode,
  CrashSurface,
  Decision,
  DedupEntry,
} from '../types/crashTypes';
import {
  canShowDialog,
  noteDialogShown,
  reset as resetCircuitBreaker,
} from './crashCircuitBreaker';

// =============================================================================
// Session-scoped state (module-level)
// =============================================================================

const signatures = new Map<string, DedupEntry>();
let dialogOpen = false;
let toastShownThisSession = false;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Canonical dedup signature for a crash context. Format is
 * `${category}::${sanitizedMessage}` — exported so every caller (listeners,
 * payload builders, tests) derives the same key.
 */
export const signatureFor = (context: CrashContext): string =>
  `${context.error.category}::${context.error.message}`;

const getOrCreateEntry = (signature: string): { entry: DedupEntry; isFirst: boolean } => {
  const existing = signatures.get(signature);
  if (existing) {
    existing.count += 1;
    return { entry: existing, isFirst: false };
  }
  const entry: DedupEntry = { firstSeen: Date.now(), count: 1, stance: 'unseen' };
  signatures.set(signature, entry);
  return { entry, isFirst: true };
};

// =============================================================================
// Public API
// =============================================================================

/**
 * Record the crash and return what to do with it. Always mutates state — even
 * when the decision is `silent_drop` the signature count still increments so
 * the session-end summary captures the true volume.
 */
export const recordAndDecide = (
  context: CrashContext,
  mode: CrashReportMode,
  surface: CrashSurface,
): Decision => {
  const signature = signatureFor(context);
  const { entry, isFirst } = getOrCreateEntry(signature);
  const isErrorBoundary = surface === 'error-boundary';

  if (mode === 'never') {
    return isErrorBoundary
      ? { kind: 'show_recovery_only', alsoSilentSend: false }
      : { kind: 'silent_drop' };
  }

  if (mode === 'always') {
    return isErrorBoundary
      ? { kind: 'show_recovery_only', alsoSilentSend: true }
      : { kind: 'silent_send' };
  }

  // mode === 'ask'.
  // `canShowDialog` is the circuit breaker: after MAX_DIALOGS_PER_SIGNATURE the
  // same signature stops opening a dialog and degrades to the repeat path
  // (recovery / silent), so a storming crash can't keep popping dialogs.
  if (isErrorBoundary) {
    if (isFirst && !dialogOpen && canShowDialog(signature)) {
      noteDialogShown(signature);
      return { kind: 'show_dialog' };
    }
    return {
      kind: 'show_recovery_only',
      alsoSilentSend: entry.stance === 'sent',
    };
  }

  // Global listener / main-process in 'ask' mode
  if (isFirst && !dialogOpen && canShowDialog(signature)) {
    noteDialogShown(signature);
    return { kind: 'show_dialog' };
  }
  if (entry.stance === 'sent') {
    return { kind: 'silent_send' };
  }
  return { kind: 'silent_drop' };
};

/** Record the user's action for a signature. No-op if signature is unknown. */
export const recordStance = (
  signature: string,
  stance: 'sent' | 'ignored',
): void => {
  const entry = signatures.get(signature);
  if (entry) {
    entry.stance = stance;
  }
};

/** Mirror the CrashDialog's open/closed state so dedup can avoid stacking. */
export const setDialogOpen = (open: boolean): void => {
  dialogOpen = open;
};

/** Return stats for a single signature, or undefined if unseen. */
export const getStats = (signature: string): { count: number } | undefined => {
  const entry = signatures.get(signature);
  return entry ? { count: entry.count } : undefined;
};

/** Return stats for every signature. Used for the session-end summary event. */
export const getAllStats = (): Array<{
  signature: string;
  count: number;
  stance: DedupEntry['stance'];
}> =>
  Array.from(signatures.entries()).map(([signature, entry]) => ({
    signature,
    count: entry.count,
    stance: entry.stance,
  }));

/**
 * Returns true exactly once per app launch — used by listeners to decide
 * whether to show the "A crash report was sent automatically." toast.
 *
 * The caller is responsible for calling `markAutoSendToastShown()` after it
 * actually displays the toast; keeping the "check" and "mark" calls separate
 * lets the caller bail out (e.g. no toast implementation available in test
 * mode) without accidentally burning the one-shot.
 */
export const shouldShowAutoSendToast = (): boolean => !toastShownThisSession;

export const markAutoSendToastShown = (): void => {
  toastShownThisSession = true;
};

/**
 * Build the `CrashReportEnrichment` metadata attached to a send-report IPC
 * call. Centralised here so every listener produces identical enrichment
 * shape and occurrence counts come from the same dedup state.
 *
 * `reportMode: 'never'` is coerced to `'ask'` because a send only happens
 * after an affirmative user decision — `never` never reaches a send path.
 */
export const buildReportEnrichment = (
  context: CrashContext,
  reportMode: CrashReportMode,
  sendPath: 'dialog' | 'silent',
): CrashReportEnrichment => {
  const stats = getStats(signatureFor(context));
  return {
    occurrenceCount: stats?.count ?? 1,
    reportMode: reportMode === 'never' ? 'ask' : reportMode,
    sendPath,
  };
};

/**
 * Build the unified `app:crash-occurred` payload from a CrashContext + decision.
 * Centralised here so all three listeners produce identical event shapes.
 *
 * Returns a concrete `CrashOccurredPayload`. The analytics bridge accepts
 * `Record<string, string | number | boolean | undefined>`, so the payload
 * assigns to it at the call site — the concrete type just guarantees that
 * the runtime shape matches the `AppCrashOccurredPayload` contract declared
 * in `electron/analytics/analyticsTypes.ts` (empty string means absent).
 */
export const buildCrashOccurredPayload = (args: CrashOccurredArgs): CrashOccurredPayload => {
  const signature = signatureFor(args.context);
  const stats = getStats(signature);
  const count = stats?.count ?? 1;
  const lastBc = args.context.breadcrumbs[args.context.breadcrumbs.length - 1];
  const messagePrefix = args.context.error.message.slice(0, 120);

  return {
    category: args.context.error.category,
    surface: args.surface,
    source: args.source,
    reportMode: args.reportMode,
    isRepeat: count > 1,
    occurrenceCount: count,
    dialogShown: args.dialogShown,
    reportSent: args.reportSent,
    errorName: args.errorName,
    messagePrefix,
    activeTabType: args.context.workspace.activeTabType ?? '',
    activeTabEngine: args.context.workspace.activeTabEngine ?? '',
    connectionEnginesCount: args.context.connection.connectionEngines.length,
    sessionAgeMs: args.context.metrics.sessionAge,
    memoryMb: args.context.metrics.memoryUsageMB,
    lastBreadcrumb: lastBc ? `${lastBc.action}:${lastBc.target}` : '',
  };
};

/** Test-only: clear all state (including the shared circuit-breaker counters). */
export const reset = (): void => {
  signatures.clear();
  dialogOpen = false;
  toastShownThisSession = false;
  resetCircuitBreaker();
};
